'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import type { Logger } from '@core/be/platform/logging/Logger';
import { computeMindmapLayoutAsync } from '@/features/mindmap/core/MindmapLayout';
import { buildReactFlowNodes } from '@/features/mindmap/core/MindmapTransform';
import type { Mindmap, MindmapLayoutDirection } from '@/features/mindmap/core/MindmapTypes';

export interface UseMindmapLayoutOptions {
  doc: Mindmap;
  previewMindmap: Mindmap | null;
  layoutPreview: MindmapLayoutDirection | null;
  editingId: string | null;
  renameLogger: Logger;
  /** Optional drag preview to influence live layout while dragging */
  dragPreview?: {
    type: 'sibling-slot' | 'parent-zone';
    parentId: string | null;
    index: number;
    draggedId: string;
    ghostSize?: { width: number; height: number };
  } | null;
}

interface MindmapPositions {
  [id: string]: { x: number; y: number };
}

interface MindmapDimensions {
  [id: string]: { width: number; height: number };
}

const positionsAreEqual = (a: MindmapPositions, b: MindmapPositions): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    const pa = a[key];
    const pb = b[key];
    if (!pb) return false;
    if (!pa) return false;
    if (pa.x !== pb.x || pa.y !== pb.y) return false;
  }
  return true;
};

export interface UseMindmapLayoutResult {
  activeMindmap: Mindmap;
  baseNodes: ReturnType<typeof buildReactFlowNodes>;
  positions: MindmapPositions;
  nodeDimensions: MindmapDimensions;
  setNodeDimensions: Dispatch<SetStateAction<MindmapDimensions>>;
  exitingNodes: Set<string>;
  resetLayoutState: (next: Mindmap) => void;
}

export function resolveActiveMindmap(
  doc: Mindmap,
  previewDoc: Mindmap | null,
  layoutPreview: MindmapLayoutDirection | null
): Mindmap {
  const base = previewDoc ?? doc;
  if (!layoutPreview) return base;
  const currentDirection = base.layoutDirection ?? 'right';
  if (currentDirection === layoutPreview) return base;
  return { ...base, layoutDirection: layoutPreview };
}

export function useMindmapLayout({
  doc,
  previewMindmap,
  layoutPreview,
  editingId,
  renameLogger,
  dragPreview = null,
}: UseMindmapLayoutOptions): UseMindmapLayoutResult {
  const [positions, setPositions] = useState<MindmapPositions>({});
  const [nodeDimensions, setNodeDimensions] = useState<MindmapDimensions>({});
  const [exitingNodes, setExitingNodes] = useState<Set<string>>(new Set());

  const exitTimerRef = useRef<number | null>(null);
  const prevVisibleRef = useRef<Set<string>>(new Set(doc.nodes.map((node) => node.id)));
  const rootAnchorRef = useRef<{ x: number; y: number } | null>(null);
  const positionsRef = useRef<MindmapPositions>(positions);

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (editingId) {
      renameLogger.debug('skip layout during edit: id=%s', editingId);
      return;
    }

    let cancelled = false;
    const direction = layoutPreview ?? doc.layoutDirection ?? 'right';

    const layoutSource = (() => {
      const source = doc;
      const idToNode = new Map(source.nodes.map((n) => [n.id, n]));
      const isHidden = (id: string): boolean => {
        let cur = idToNode.get(id);
        while (cur && cur.parentId) {
          const parent = idToNode.get(cur.parentId);
          if (!parent) break;
          if (parent.collapsed) return true;
          cur = parent;
        }
        return false;
      };
      const visibleNodes = source.nodes.filter((n) => !isHidden(n.id));

      // If we have a drag preview for a sibling-slot, build a temporary layout source
      // that excludes the dragged node and includes a virtual ghost placeholder.
      if (dragPreview && dragPreview.type === 'sibling-slot') {
        const withoutDragged = visibleNodes.filter((n) => n.id !== dragPreview.draggedId);
        const ghostNode = {
          id: '__ghost__',
          text: '',
          parentId: dragPreview.parentId,
          order: dragPreview.index,
          collapsed: false,
        } as unknown as import('@/features/mindmap/core/MindmapTypes').Mindmap['nodes'][number];
        return { ...source, nodes: [...withoutDragged, ghostNode] };
      }

      return { ...source, nodes: visibleNodes };
    })();

    const nextVisibleSet = new Set(layoutSource.nodes.map((n) => n.id));
    const prevVisibleSet = prevVisibleRef.current;
    const exiting: string[] = [];
    prevVisibleSet.forEach((id) => {
      if (!nextVisibleSet.has(id)) exiting.push(id);
    });

    if (exitTimerRef.current != null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (exiting.length) {
      setExitingNodes((prev) => {
        const merged = new Set(prev);
        for (const id of exiting) merged.add(id);
        return merged;
      });
    }

    (async () => {
      try {
        // Augment dimensions with ghost size (if any) so ELK can account for placeholder size
        const augmentedDims = (() => {
          if (dragPreview && dragPreview.type === 'sibling-slot' && dragPreview.ghostSize) {
            return { ...nodeDimensions, ['__ghost__']: { ...dragPreview.ghostSize } } as MindmapDimensions;
          }
          return nodeDimensions;
        })();

        const result = await computeMindmapLayoutAsync(layoutSource, direction, augmentedDims);
        if (cancelled) return;

        const nextPositions: MindmapPositions = { ...result };
        const previousPositions = positionsRef.current;

        if (!nextPositions[doc.rootId]) {
          nextPositions[doc.rootId] = previousPositions[doc.rootId] ?? { x: 0, y: 0 };
        }

        const prevRoot = rootAnchorRef.current;
        const nextRoot = nextPositions[doc.rootId];
        if (prevRoot && nextRoot) {
          const dx = prevRoot.x - nextRoot.x;
          const dy = prevRoot.y - nextRoot.y;
          for (const key of Object.keys(nextPositions)) {
            nextPositions[key] = {
              x: nextPositions[key].x + dx,
              y: nextPositions[key].y + dy,
            };
          }
          nextPositions[doc.rootId] = { ...prevRoot };
        }

        const applyPositions = (candidate: MindmapPositions) => {
          if (positionsAreEqual(positionsRef.current, candidate)) {
            return false;
          }
          setPositions(candidate);
          return true;
        };

        let effectivePositions: MindmapPositions = nextPositions;

        if (exiting.length) {
          const idToNode = new Map(doc.nodes.map((n) => [n.id, n]));
          const merged: MindmapPositions = { ...nextPositions };
          for (const id of exiting) {
            const node = idToNode.get(id);
            const parentId = node?.parentId ?? null;
            const parentPos = (parentId && merged[parentId]) ||
              (parentId && previousPositions[parentId]) ||
              previousPositions[doc.rootId] || { x: 0, y: 0 };
            merged[id] = { x: parentPos.x, y: parentPos.y };
          }
          effectivePositions = merged;
          applyPositions(merged);
          exitTimerRef.current = window.setTimeout(() => {
            setExitingNodes(new Set());
            exitTimerRef.current = null;
          }, 260);
        } else {
          applyPositions(nextPositions);
        }

        const anchor = effectivePositions[doc.rootId];
        if (anchor) {
          rootAnchorRef.current = { ...anchor };
        }

        prevVisibleRef.current = nextVisibleSet;
      } catch (error) {
        console.error('[Mindmap] ELK layout failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [doc, layoutPreview, nodeDimensions, editingId, renameLogger]);

  const activeMindmapSource = useMemo(
    () => resolveActiveMindmap(doc, previewMindmap, layoutPreview),
    [doc, previewMindmap, layoutPreview]
  );

  const activeMindmap = useMemo(() => {
    const source = activeMindmapSource;
    const idToNode = new Map(source.nodes.map((n) => [n.id, n]));
    const isHidden = (id: string): boolean => {
      let cur = idToNode.get(id);
      while (cur && cur.parentId) {
        const parent = idToNode.get(cur.parentId);
        if (!parent) break;
        if (parent.collapsed) return true;
        cur = parent;
      }
      return false;
    };
    const visibleNodes = source.nodes.filter((n) => !isHidden(n.id) || exitingNodes.has(n.id));
    return { ...source, nodes: visibleNodes };
  }, [activeMindmapSource, exitingNodes]);

  const baseNodes = useMemo(
    () => buildReactFlowNodes(activeMindmap, positions, { mapId: activeMindmap.id, offset: { x: 0, y: 0 } }),
    [activeMindmap, positions]
  );

  const resetLayoutState = useCallback((next: Mindmap) => {
    if (exitTimerRef.current != null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setPositions({});
    positionsRef.current = {};
    setNodeDimensions({});
    setExitingNodes(new Set());
    prevVisibleRef.current = new Set(next.nodes.map((node) => node.id));
    rootAnchorRef.current = null;
  }, []);

  return {
    activeMindmap,
    baseNodes,
    positions,
    nodeDimensions,
    setNodeDimensions,
    exitingNodes,
    resetLayoutState,
  };
}
